import type { ToggleName } from '@tilelist/domain/experiments/Toggles.ts';

declare global {
    namespace App {
        interface Onex {
            DUMMY: 'groupA' | 'groupB';
            DUMMY_N_GROUP: 'groupA' | 'groupB' | 'groupC';
            E3694: 'E3694A' | 'E3694B';
            E3799: 'E3799A' | 'E3799B' | 'E3799C';
            E3776: 'E3776A' | 'E3776B';
            E3695: 'E3695A' | 'E3695B';
            E3738: 'E3738A' | 'E3738B';
            E3772: 'StatusQuo' | 'Testgroup';
            E3822: 'E3822A' | 'E3822B';
            E3804: 'E3804A' | 'E3804B';
            E3801: 'E3801A' | 'E3801B';
            E3823: 'E3823A' | 'E3823B' | 'E3823C' | 'E3823D' | 'E3823E';
        }
    }
}

type OnexExperimentId = keyof App.Onex;
type OnexGroup<Id extends OnexExperimentId> = App.Onex[Id];
export type OnexAssignments = Partial<{ [K in OnexExperimentId]: App.Onex[K] }>;

/**
 *
 */
export const EXPERIMENT_ONEX_IDS = {
    DUMMY: 'DUMMY',
    DUMMY_N_GROUP: 'DUMMY_N_GROUP',
    ADDITIONAL_ASSORTMENTS_FOR_TOP_INFOS: 'E3694',
    ADDITIONAL_IMAGES: 'E3799',
    SKELETON_LOADING: 'E3776',
    COMPARISON_ADDITIONAL_CATEGORIES: 'E3695',
    TOPIC_PAGES_FILTER_INTEGRATION: 'E3738',
    TEASER_VARIATION_BOOST: 'E3772',
    GALLERY_LANDSCAPE_TWO_COLUMN: 'E3822',
    BRAND_TILE: 'E3804',
    CLEAN_TILES: 'E3801',
    UVP_DISPLAY: 'E3823',
} as const satisfies Record<string, OnexExperimentId>;

export type ExperimentId = keyof typeof EXPERIMENT_ONEX_IDS;

export type ExperimentOnexId = (typeof EXPERIMENT_ONEX_IDS)[ExperimentId];

export type ExperimentGroups = {
    [K in ExperimentId]: OnexGroup<(typeof EXPERIMENT_ONEX_IDS)[K]>;
};

/**
 *
 */
export interface ExperimentConfig<E extends keyof ExperimentGroups> {
    /**
 *
 */
    onexExperimentId: (typeof EXPERIMENT_ONEX_IDS)[E];

    /**
 *
 *
 *
 */
    experimentToggle: ToggleName;

    /**
 *
 *
 *
 *
 */
    featureToggle: ToggleName;

    /**
 *
 *
 */
    groups: ReadonlyArray<ExperimentGroups[E]>;

    /**
 *
 *
 */
    defaultGroup: ExperimentGroups[E];

    /**
 *
 *
 */
    featureOnGroup: ExperimentGroups[E];
}

export type ExperimentConfigs = {
    [K in keyof ExperimentGroups]: ExperimentConfig<K>;
};

/**
 *
 *
 *
 *
 *
 *
 *
 */
export const EXPERIMENTS = {
    DUMMY: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.DUMMY,
        experimentToggle: 'EXPERIMENT_DUMMY_ACTIVE',
        featureToggle: 'FEATURE_DUMMY_USE_GROUP_A',
        groups: ['groupA', 'groupB'],
        defaultGroup: 'groupB',
        featureOnGroup: 'groupA',
    },
    DUMMY_N_GROUP: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.DUMMY_N_GROUP,
        experimentToggle: 'EXPERIMENT_DUMMY_N_GROUP_ACTIVE',
        featureToggle: 'FEATURE_DUMMY_N_GROUP_USE_GROUP_B',
        groups: ['groupA', 'groupB', 'groupC'],
        defaultGroup: 'groupA',
        featureOnGroup: 'groupB',
    },
    ADDITIONAL_ASSORTMENTS_FOR_TOP_INFOS: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.ADDITIONAL_ASSORTMENTS_FOR_TOP_INFOS,
        experimentToggle: 'REPTILE_1472_TOP_INFO_ADDITIONAL_ASSORTMENTS_EXPERIMENT',
        featureToggle: 'REPTILE_1472_TOP_INFO_ADDITIONAL_ASSORTMENTS',
        groups: ['E3694A', 'E3694B'],
        defaultGroup: 'E3694A',
        featureOnGroup: 'E3694B',
    },
    ADDITIONAL_IMAGES: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.ADDITIONAL_IMAGES,
        experimentToggle: 'REPTILE_1522_ADDITIONAL_IMAGES_EXPERIMENT',
        featureToggle: 'REPTILE_1522_ADDITIONAL_IMAGES',
        groups: ['E3799A', 'E3799B', 'E3799C'],
        defaultGroup: 'E3799A',
        featureOnGroup: 'E3799B',
    },
    SKELETON_LOADING: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.SKELETON_LOADING,
        experimentToggle: 'REPTILE_1543_SKELETON_LOADING_EXPERIMENT',
        featureToggle: 'REPTILE_1543_SKELETON_LOADING',
        groups: ['E3776A', 'E3776B'],
        defaultGroup: 'E3776A',
        featureOnGroup: 'E3776B',
    },
    COMPARISON_ADDITIONAL_CATEGORIES: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.COMPARISON_ADDITIONAL_CATEGORIES,
        experimentToggle: 'REPTILE_1529_COMPARISON_ADDITIONAL_CATEGORIES_EXPERIMENT',
        featureToggle: 'REPTILE_1529_COMPARISON_ADDITIONAL_CATEGORIES',
        groups: ['E3695A', 'E3695B'],
        defaultGroup: 'E3695A',
        featureOnGroup: 'E3695B',
    },
    TOPIC_PAGES_FILTER_INTEGRATION: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.TOPIC_PAGES_FILTER_INTEGRATION,
        experimentToggle: 'REPTILE_1560_TOPIC_PAGES_FILTER_INTEGRATION_EXPERIMENT',
        featureToggle: 'REPTILE_1560_TOPIC_PAGES_FILTER_INTEGRATION',
        groups: ['E3738A', 'E3738B'],
        defaultGroup: 'E3738A',
        featureOnGroup: 'E3738B',
    },
    TEASER_VARIATION_BOOST: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.TEASER_VARIATION_BOOST,
        experimentToggle: 'REPTILE_1564_BOOST_TEASER_VARIATIONS_EXPERIMENT',
        featureToggle: 'REPTILE_1564_BOOST_TEASER_VARIATIONS',
        groups: ['StatusQuo', 'Testgroup'],
        defaultGroup: 'StatusQuo',
        featureOnGroup: 'Testgroup',
    },
    GALLERY_LANDSCAPE_TWO_COLUMN: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.GALLERY_LANDSCAPE_TWO_COLUMN,
        experimentToggle: 'REPTILE_1590_TWO_COLUMN_LANDSCAPE_EXPERIMENT',
        featureToggle: 'REPTILE_1590_TWO_COLUMN_LANDSCAPE',
        groups: ['E3822A', 'E3822B'],
        defaultGroup: 'E3822A',
        featureOnGroup: 'E3822B',
    },
    BRAND_TILE: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.BRAND_TILE,
        experimentToggle: 'REPTILE_1537_BRAND_TILE_EXPERIMENT',
        featureToggle: 'REPTILE_1537_BRAND_TILE',
        groups: ['E3804A', 'E3804B'],
        defaultGroup: 'E3804A',
        featureOnGroup: 'E3804B',
    },
    CLEAN_TILES: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.CLEAN_TILES,
        experimentToggle: 'REPTILE_1562_CLEAN_TILES_EXPERIMENT',
        featureToggle: 'REPTILE_1562_CLEAN_TILES',
        groups: ['E3801A', 'E3801B'],
        defaultGroup: 'E3801A',
        featureOnGroup: 'E3801B',
    },
    UVP_DISPLAY: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.UVP_DISPLAY,
        experimentToggle: 'REPTILE_1608_UVP_DISPLAY_EXPERIMENT',
        featureToggle: 'REPTILE_1608_UVP_DISPLAY',
        groups: ['E3823A', 'E3823B', 'E3823C', 'E3823D', 'E3823E'],
        defaultGroup: 'E3823A',
        featureOnGroup: 'E3823A', /*                                                          */
    },
} as const satisfies ExperimentConfigs;

export type AdditionalImagesType = 'Single' | 'CarouselV1' | 'CarouselV2';
